/*!
 *   Copyright 2022  SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

@import "../../../node_modules/@senx/discovery-widgets/dist/discovery/scss/vars.scss";
@import '../../../node_modules/leaflet/dist/leaflet.css';

:host {
  display: block;
  width: 100%;

  .map-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    align-content: stretch;
  }

  .leaflet-top, .leaflet-bottom {
    z-index: 900;
  }

  .leaflet-container {
    width: 100%;
    height: 100%;
  }

  .slider-bounds {
    align-self: center;
    padding: 0.25rem;
  }

  button.discovery-btn {
    margin: .25rem .125rem;
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    font-size: var(--warp-view-button-font-size, 1rem);
    border: 1px solid var(--warp-view-button-border-color, $w10-blue);
    padding: var(--warp-view-button-padding, .375rem .75rem);
    color: var(--warp-view-button-label-color, $w10-white);
    background-color: var(--warp-view-button-bg-color, $w10-blue);
    min-width: var(--warp-view-button-width, auto);
    border-radius: var(--warp-view-button-border-radius, 0.25rem);
    width: var(--warp-view-button-width, 30px);
    height: var(--warp-view-button-width, 30px);
    position: relative;

    &:before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      width: var(--warp-view-button-width, 30px);
      height: var(--warp-view-button-width, 30px);
      background-size: 80%;
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  .paused:before {
    background-image: var(--warp-view-button-play, url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAt0lEQVRYw+3WMQrCMBSH8T8iDk4OUkQ8gLdydfMMTiIiIiK6e46ewqMEKbp8nYIBWxBK8obmmwv9DcnLk3K5XEvAEngAW2BqASj55oBdUgjw5DcH7IHCCuB7AUdgZgUIIacokD8Bvgo4A3MrQAi5AAsrQAi5doJ0BPjewA2YtP1nGPkyjSStJY0lrZo+GFhP3NiAj6S7pE1vDqHZNTQbRGaj2OwxcsAh1XNcNuwBSRcS25Usl+tNNRz/81QsYfwRAAAAAElFTkSuQmCC'));
  }

  .play:before {
    background-image: var(---warp-view-button-pause, url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAlUlEQVRYw+2WoRHCQBBF395ERFALChVNJ5EpJBWgMkxkOqEAakGgsrEZZrmbi0Hwn/z35/aJFQtC/DuWe3T3DrgB7S5+Ab2ZPT+6Z+AOnHbxGxjM7HHIzt0njxmD7vilO+VmpIJDqsjTkRnp1zsgAQlIQAISkIAESgJrRb5W/gFAUxCYgUtwki1BdwGuwUk26/IVIscG42BLPBOIOI4AAAAASUVORK5CYII='));
  }

  .commands-wrapper {
    flex-shrink: 1;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .slider-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 10px;
  }

  .map-wrapper {
    flex-grow: 1;
    z-index: 1;
  }

  table {
    width: 100%;
    color: var(--warp-view-font-color, $w10-charcoal);

    td, th {
      padding: var(--warp-view-datagrid-cell-padding, 5px);
      text-align: left;
    }

    .odd {
      background-color: var(--warp-view-datagrid-odd-bg-color, $w10-white);
      color: var(--warp-view-datagrid-odd-color, $w10-charcoal);
    }

    .even {
      background-color: var(--warp-view-datagrid-even-bg-color, $w10-lightgrey);
      color: var(--warp-view-datagrid-even-color, $w10-charcoal);
    }
  }

  div.modal {
    display: none; /* Hidden by default */
    position: absolute; /* Stay in place */
    z-index: 3; /* Sit on top */
    left: 0;
    top: 0;
    width: 100%; /* Full width */
    height: 100%; /* Full height */
    overflow: auto; /* Enable scroll if needed */
    background-color: rgb(0, 0, 0); /* Fallback color */
    background-color: rgba(0, 0, 0, 0.4); /* Black w/ opacity */

    .modal-content {
      background-color: #fefefe;
      margin: 5% auto;
      padding: 20px;
      border: 1px solid #888;
      min-height: 80%;
      width: 80%; /* Could be more or less, depending on screen size */
      position: relative;

      .modal-wrapper {
        height: calc(100% - 40px);
        width: calc(100% - 40px);
        position: absolute;

        .discovery-dashboard-tile {
          width: 100%;
          height: 100%;

          & > div {
            width: 100%;
            height: 100%;
            margin-bottom: -20px;
          }
        }
      }
    }

    .close {
      color: #aaa;
      font-size: 28px;
      font-weight: bold;
      position: absolute;
      top: 0;
      right: 20px;
      z-index: 900;

      &:hover, &:focus {
        color: black;
        text-decoration: none;
        cursor: pointer;
      }
    }
  }
}
